// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/DriftStatePage.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import {
  Button,
  Card,
  Section,
  StatusChip,
} from "../design-system/components";
import "../dashboard.css";
import DriftIntelligencePanel from "./drift/DriftIntelligencePanel";

const REQUEST_TIMEOUT_MS = 30000;
const FRAMEWORKS = [
  "SOC2",
  "NIST",
  "ISO",
  "FedRAMP",
  "FDA",
  "AML/KYC",
  "CCPA",
  "OSHA",
];

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}


function cleanDisplayValue(value, fallback = "—") {
  const text = String(value ?? "").trim();
  return text ? text : fallback;
}

function formatTimestamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatRelativeTime(value) {
  if (!value) return "—";

  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return "—";

  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatDuration(value) {
  const seconds = safeNumber(value, NaN);
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const remSeconds = seconds % 60;
    return remSeconds ? `${minutes}m ${remSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) {
    return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

function buildDisplayTitle(controlId, ruleId) {
  const left = cleanDisplayValue(controlId);
  const right = cleanDisplayValue(ruleId);

  if (left === "—" && right === "—") return "Unnamed drift item";
  if (left === right) return left;
  if (left === "—") return right;
  if (right === "—") return left;
  return `${left} · ${right}`;
}

function buildEvidenceLabel(resourceId) {
  return cleanDisplayValue(resourceId);
}

function severityTone(value) {
  const severity = String(value || "").trim().toLowerCase();
  if (severity === "critical") return "danger";
  if (severity === "high") return "warning";
  if (severity === "low") return "success";
  return "informational";
}

function stateTone(value) {
  const state = String(value || "").trim().toLowerCase();
  if (["pass", "healthy", "compliant"].includes(state)) return "success";
  if (["fail", "drifted", "noncompliant"].includes(state)) return "danger";
  if (["stale", "warning"].includes(state)) return "warning";
  return "informational";
}

function SeverityPill({ value }) {
  const label = String(value || "info").trim().replace(/_/g, " ");
  return <StatusChip tone={severityTone(value)}>{label}</StatusChip>;
}

function StatePill({ value }) {
  const label = String(value || "unknown").trim().replace(/_/g, " ");
  return <StatusChip tone={stateTone(value)}>{label}</StatusChip>;
}

function SourcePill({ value }) {
  const source = String(value || "").toLowerCase();
  const label =
    source === "drift_event"
      ? "Event"
      : source === "native_interval"
      ? "Native"
      : source === "identity_finding"
      ? "Identity"
      : source === "code_finding"
      ? "Code"
      : source === "edge_finding"
      ? "Edge"
      : "Snapshot";

  return <StatusChip tone="neutral">{label}</StatusChip>;
}

function MetricCard({ label, value, footnote, tone = "default" }) {
  return (
    <div className={`drift-metric drift-metric--${tone}`}>
      <span className="drift-metric__label">{label}</span>
      <strong className="drift-metric__value">{value}</strong>
      {footnote ? <span className="drift-metric__supporting">{footnote}</span> : null}
    </div>
  );
}

function MetaField({ label, value, wrap = false }) {
  return (
    <div className="drift-meta-field">
      <span>{label}</span>
      <strong className={wrap ? "is-wrap" : ""} title={String(value || "—")}>
        {value || "—"}
      </strong>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="drift-filter-group">
      <span className="drift-filter-group__label">{label}</span>
      <div className="drift-filter-group__controls">{children}</div>
    </div>
  );
}

function normalizeLegacyDriftEvents(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.events)) return payload.events;
  return [];
}

function normalizeControlStatus(payload) {
  if (Array.isArray(payload?.controls)) return payload.controls;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeFreshness(payload) {
  if (Array.isArray(payload?.rules)) return payload.rules;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeNativeHistory(payload) {
  if (Array.isArray(payload?.intervals)) return payload.intervals;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeNativeSummary(payload) {
  return payload && typeof payload === "object" ? payload : {};
}

function normalizeIdentityFindings(payload) {
  if (Array.isArray(payload?.findings)) return payload.findings;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeIdentitySource(value = "") {
  const source = String(value || "").toLowerCase();
  if (source === "google_workspace") return "Google Workspace";
  if (source === "okta") return "Okta";
  if (source === "github") return "GitHub";
  if (source === "cloudflare") return "Cloudflare";
  if (source === "code_finding") return "GitHub";
  if (source === "edge_finding") return "Cloudflare";
  return cleanDisplayValue(value, "Identity");
}

function isIdentityFinding(row = {}) {
  const source = String(row?.source || row?.provider || "").toLowerCase();
  const stream = String(row?.stream || "").toLowerCase();
  const rule = String(row?.rule_id || row?.ruleId || "").toUpperCase();

  return (
    source === "google_workspace" ||
    source === "okta" ||
    stream === "google_workspace_directory" ||
    stream === "okta_identity_inventory" ||
    rule.startsWith("GW_") ||
    rule.startsWith("OKTA_")
  );
}

function getIdentitySeverity(row = {}) {
  const state = String(row?.state || "").toLowerCase();
  const rule = String(row?.rule_id || row?.ruleId || "").toUpperCase();

  if (state === "fail" || state === "failed") {
    if (
      rule.includes("2SV") ||
      rule.includes("MFA") ||
      rule.includes("ADMIN") ||
      rule.includes("PRIVILEGED") ||
      rule.includes("SUPER_ADMIN")
    ) {
      return "high";
    }
    return "medium";
  }

  if (state === "pass" || state === "passed") return "low";
  return "info";
}

function getIdentitySummary(row = {}) {
  const source = normalizeIdentitySource(row?.source || row?.provider);
  const rule = cleanDisplayValue(row?.rule_id || row?.ruleId, "identity finding");
  const state = cleanDisplayValue(row?.state, "unknown");
  const resource = cleanDisplayValue(row?.resource_id || row?.resourceId, "affected identity");

  if (String(rule).toUpperCase() === "GW_2SV_DISABLED") {
    return `Google Workspace 2-Step Verification is ${state} for ${resource}.`;
  }

  if (String(rule).toUpperCase() === "OKTA_MFA_DISABLED") {
    return `Okta MFA posture is ${state} for ${resource}.`;
  }

  if (String(rule).toUpperCase().includes("STALE_ADMIN")) {
    return `${source} stale administrator posture is ${state} for ${resource}.`;
  }

  return `${source} identity finding ${rule} is ${state} for ${resource}.`;
}

function findIdentityFreshnessMatch(ruleId, framework, freshnessRows = []) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  const normalizedFramework = String(framework || "");

  if (!normalizedRule) return null;

  return (
    freshnessRows.find(
      (freshnessRow) =>
        String(freshnessRow.framework || "") === normalizedFramework &&
        String(freshnessRow.rule_id || "").toUpperCase() === normalizedRule
    ) ||
    freshnessRows.find(
      (freshnessRow) =>
        String(freshnessRow.rule_id || "").toUpperCase() === normalizedRule
    ) ||
    null
  );
}

function findIdentityStatusMatch(controlId, framework, controlStatusRows = []) {
  const normalizedControl = String(controlId || "");
  const normalizedFramework = String(framework || "");

  if (!normalizedControl) return null;

  return (
    controlStatusRows.find(
      (statusRow) =>
        String(statusRow.framework || "") === normalizedFramework &&
        String(statusRow.control_id || "") === normalizedControl
    ) ||
    controlStatusRows.find(
      (statusRow) => String(statusRow.control_id || "") === normalizedControl
    ) ||
    null
  );
}

function buildIdentityTimeline(identityFindings = [], controlStatusRows = [], freshnessRows = [], frameworkHint = "SOC2") {
  const activeIdentityFindings = identityFindings.filter(isIdentityFinding);

  return activeIdentityFindings.map((row, index) => {
    const ruleId = row.rule_id || row.ruleId || "identity_finding";
    const framework = row.framework || frameworkHint || "SOC2";
    const freshnessMatch = findIdentityFreshnessMatch(ruleId, framework, freshnessRows);

    const controlId =
      row.control_id ||
      row.controlId ||
      freshnessMatch?.control_id ||
      freshnessMatch?.controlId ||
      "Identity";

    const statusMatch = findIdentityStatusMatch(controlId, framework, controlStatusRows);

    const resourceId = row.resource_id || row.resourceId || "—";
    const source = row.source || row.provider || "identity";
    const stream = row.stream || "identity_inventory";
    const currentState = row.state || "unknown";

    const lastSeen =
      row.last_seen_at ||
      row.updated_at ||
      row.created_at ||
      statusMatch?.last_checked_at ||
      freshnessMatch?.last_seen_at ||
      null;

    return {
      id: `identity-${source}-${stream}-${ruleId}-${resourceId}-${index}`,
      source_type: "identity_finding",
      display_control_label: "Identity",
      framework,
      control_id: controlId,
      rule_id: ruleId,
      resource_id: resourceId,
      provider: source,
      stream,
      previous_state: "observed",
      current_state: currentState,
      freshness_state:
        row.freshness_status ||
        row.freshness_state ||
        statusMatch?.freshness_state ||
        freshnessMatch?.freshness_status ||
        "fresh",
      severity: getIdentitySeverity(row),
      started_at: lastSeen,
      ended_at: null,
      last_seen_at: lastSeen,
      duration_seconds: null,
      summary: getIdentitySummary(row),
      open_findings_total:
        String(currentState || "").toLowerCase() === "fail"
          ? Math.max(1, safeNumber(statusMatch?.open_findings_total, 1))
          : safeNumber(statusMatch?.open_findings_total, 0),
      transition_type: "identity_posture",
      native_is_open: String(currentState || "").toLowerCase() === "fail",
      raw: row,
    };
  });
}


const CODE_EDGE_FINDING_RULE_LABELS = {
  GH_PUBLIC_REPOSITORY: "GitHub public repository",
  GH_BRANCH_PROTECTION_DISABLED: "GitHub branch protection disabled or unreadable",
  GH_REQUIRED_REVIEWS_DISABLED: "GitHub required reviews disabled or unreadable",
  GH_ADMIN_BYPASS_ENABLED: "GitHub admin bypass enabled or unreadable",
  GH_SECRET_SCANNING_DISABLED: "GitHub secret scanning disabled or unreadable",
  GH_DEPENDABOT_DISABLED: "GitHub Dependabot disabled",
  CF_WAF_DISABLED: "Cloudflare WAF disabled",
  CF_TLS_MIN_VERSION_WEAK: "Cloudflare weak TLS minimum",
  CF_ALWAYS_USE_HTTPS_DISABLED: "Cloudflare HTTPS enforcement disabled",
  CF_SECURITY_LEVEL_LOW: "Cloudflare security level low",
  CF_DNSSEC_DISABLED: "Cloudflare DNSSEC disabled or unreadable",
  CF_ZONE_ACTIVITY_OBSERVED: "Cloudflare zone inventory observed",
};

const CODE_EDGE_RULE_CONTROL_BY_FRAMEWORK = {
  GH_PUBLIC_REPOSITORY: { SOC2: "CC6.6" },
  GH_BRANCH_PROTECTION_DISABLED: { SOC2: "CC8.1" },
  GH_REQUIRED_REVIEWS_DISABLED: { SOC2: "CC8.1" },
  GH_ADMIN_BYPASS_ENABLED: { SOC2: "CC8.1" },
  GH_SECRET_SCANNING_DISABLED: { SOC2: "CC6.6" },
  GH_DEPENDABOT_DISABLED: { SOC2: "CC7.1" },
  CF_WAF_DISABLED: { SOC2: "CC6.6" },
  CF_TLS_MIN_VERSION_WEAK: { SOC2: "CC6.6" },
  CF_ALWAYS_USE_HTTPS_DISABLED: { SOC2: "CC6.6" },
  CF_SECURITY_LEVEL_LOW: { SOC2: "CC7.1" },
  CF_DNSSEC_DISABLED: { SOC2: "CC6.6" },
  CF_ZONE_ACTIVITY_OBSERVED: { SOC2: "CC7.2" },
};

function parseFindingMetadata(row = {}) {
  const candidates = [row.metadata, row.details, row.payload, row.raw];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "object") return candidate;
    if (typeof candidate === "string") {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === "object") return parsed;
      } catch {
        // Ignore malformed metadata and fall back to generated summary text.
      }
    }
  }
  return {};
}

function isCodeEdgeFinding(row = {}) {
  const source = String(row?.source || row?.provider || "").toLowerCase();
  const stream = String(row?.stream || "").toLowerCase();
  const rule = String(row?.rule_id || row?.ruleId || "").toUpperCase();

  return (
    source === "github" ||
    source === "cloudflare" ||
    stream.startsWith("github") ||
    stream.startsWith("cloudflare") ||
    rule.startsWith("GH_") ||
    rule.startsWith("CF_")
  );
}

function getCodeEdgeDomain(row = {}) {
  const source = String(row?.source || row?.provider || "").toLowerCase();
  const stream = String(row?.stream || "").toLowerCase();
  const rule = String(row?.rule_id || row?.ruleId || "").toUpperCase();

  if (source === "github" || stream.startsWith("github") || rule.startsWith("GH_")) {
    return "code";
  }
  if (source === "cloudflare" || stream.startsWith("cloudflare") || rule.startsWith("CF_")) {
    return "edge";
  }
  return "code_edge";
}

function getCodeEdgeSourceLabel(row = {}) {
  const domain = getCodeEdgeDomain(row);
  if (domain === "code") return "GitHub";
  if (domain === "edge") return "Cloudflare";
  return cleanDisplayValue(row?.source || row?.provider || row?.stream, "Code / Edge");
}

function getCodeEdgeControlIdForFramework(ruleId, frameworkValue) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  const mapping = CODE_EDGE_RULE_CONTROL_BY_FRAMEWORK[normalizedRule] || {};
  const exactFramework = cleanDisplayValue(frameworkValue, "SOC2");
  return mapping[exactFramework] || mapping[exactFramework.toUpperCase()] || mapping.SOC2 || "Code / Edge";
}

function getCodeEdgeRuleLabel(ruleId) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  if (CODE_EDGE_FINDING_RULE_LABELS[normalizedRule]) return CODE_EDGE_FINDING_RULE_LABELS[normalizedRule];

  return normalizedRule
    ? normalizedRule
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Code / edge finding";
}

function getCodeEdgeSeverity(row = {}) {
  const explicit = String(row?.severity || row?.severity_label || row?.severityLabel || "").toLowerCase();
  if (["critical", "high", "medium", "low", "info"].includes(explicit)) return explicit;

  const state = String(row?.state || "").toLowerCase();
  const rule = String(row?.rule_id || row?.ruleId || "").toUpperCase();
  if (state === "fail" || state === "failed") {
    if (
      rule.includes("WAF") ||
      rule.includes("TLS") ||
      rule.includes("PUBLIC_REPOSITORY") ||
      rule.includes("BRANCH_PROTECTION") ||
      rule.includes("ADMIN_BYPASS")
    ) {
      return "high";
    }
    return "medium";
  }

  if (state === "pass" || state === "passed") return "low";
  return "medium";
}

function getCodeEdgeSummary(row = {}) {
  const metadata = parseFindingMetadata(row);
  const ruleId = row?.rule_id || row?.ruleId;
  const ruleLabel = getCodeEdgeRuleLabel(ruleId);
  const sourceLabel = getCodeEdgeSourceLabel(row);
  const state = cleanDisplayValue(row?.state, "unknown");
  const resource = cleanDisplayValue(row?.resource_id || row?.resourceId, "affected resource");

  return (
    metadata.risk_reason ||
    metadata.control_evidence ||
    row.reason_summary ||
    row.remediation_hint ||
    `${sourceLabel} ${ruleLabel.toLowerCase()} is ${state} for ${resource}.`
  );
}

function buildCodeEdgeTimeline(findings = [], controlStatusRows = [], freshnessRows = [], frameworkHint = "SOC2") {
  const activeCodeEdgeFindings = findings.filter(isCodeEdgeFinding);

  return activeCodeEdgeFindings.map((row, index) => {
    const ruleId = row.rule_id || row.ruleId || "code_edge_finding";
    const framework = row.framework || frameworkHint || "SOC2";
    const freshnessMatch = findIdentityFreshnessMatch(ruleId, framework, freshnessRows);
    const controlId =
      row.control_id ||
      row.controlId ||
      freshnessMatch?.control_id ||
      freshnessMatch?.controlId ||
      getCodeEdgeControlIdForFramework(ruleId, framework);
    const statusMatch = findIdentityStatusMatch(controlId, framework, controlStatusRows);
    const resourceId = row.resource_id || row.resourceId || "—";
    const source = row.source || row.provider || getCodeEdgeDomain(row);
    const stream = row.stream || `${getCodeEdgeDomain(row)}_posture`;
    const currentState = row.state || "unknown";
    const sourceType = getCodeEdgeDomain(row) === "edge" ? "edge_finding" : "code_finding";

    const lastSeen =
      row.last_seen_at ||
      row.updated_at ||
      row.created_at ||
      statusMatch?.last_checked_at ||
      freshnessMatch?.last_seen_at ||
      null;

    return {
      id: `${sourceType}-${source}-${stream}-${ruleId}-${resourceId}-${index}`,
      source_type: sourceType,
      display_control_label: getCodeEdgeDomain(row) === "edge" ? "Edge" : "Code",
      framework,
      control_id: controlId,
      rule_id: ruleId,
      resource_id: resourceId,
      provider: source,
      stream,
      previous_state: "observed",
      current_state: currentState,
      freshness_state:
        row.freshness_status ||
        row.freshness_state ||
        statusMatch?.freshness_state ||
        freshnessMatch?.freshness_status ||
        "fresh",
      severity: getCodeEdgeSeverity(row),
      started_at: lastSeen,
      ended_at: null,
      last_seen_at: lastSeen,
      duration_seconds: null,
      summary: getCodeEdgeSummary(row),
      open_findings_total:
        String(currentState || "").toLowerCase() === "fail"
          ? Math.max(1, safeNumber(statusMatch?.open_findings_total, 1))
          : safeNumber(statusMatch?.open_findings_total, 0),
      transition_type: `${getCodeEdgeDomain(row)}_posture`,
      native_is_open: String(currentState || "").toLowerCase() === "fail",
      raw: row,
    };
  });
}

function timelineMatchesSourceFilter(item = {}, sourceFilter = "all") {
  if (!sourceFilter || sourceFilter === "all") return true;
  if (sourceFilter === "identity") return item.source_type === "identity_finding";
  if (sourceFilter === "code") return item.source_type === "code_finding";
  if (sourceFilter === "edge") return item.source_type === "edge_finding";
  if (sourceFilter === "ec2") {
    // Count only EC2-owned drift evidence, not unrelated Config rules mentioning EC2.
    const rule = String(item.rule_id || "").toUpperCase();
    const stream = String(item.raw?.stream || item.raw?.source_stream || item.stream || "").toLowerCase();
    return rule.startsWith("EC2_") || stream === "aws_ec2_inventory";
  }
  if (sourceFilter === "drift") {
    return !["identity_finding", "code_finding", "edge_finding"].includes(item.source_type);
  }
  return true;
}

function inferSeverityFromNative(interval) {
  const sev = String(interval?.severity || "").toLowerCase();
  if (sev) return sev;

  const toState = String(interval?.status_to || "").toLowerCase();
  if (toState === "fail") return "high";
  if (toState === "unknown") return "medium";
  if (toState === "pass") return "low";
  return "info";
}

function inferFreshnessState(interval, freshnessRows, frameworkHint) {
  const framework = interval?.framework || frameworkHint || "—";
  const ruleId = interval?.rule_id || null;
  const controlId = interval?.control_id || null;

  if (!ruleId && !controlId) return "unknown";

  const match = freshnessRows.find((row) => {
    const sameFramework = String(row.framework || "") === String(framework || "");
    const sameControl =
      controlId && String(row.control_id || "") === String(controlId || "");
    const sameRule = ruleId && String(row.rule_id || "") === String(ruleId || "");
    return sameFramework && (sameRule || sameControl);
  });

  return match?.freshness_status || "unknown";
}

function inferOpenFindings(interval, controlStatusRows, frameworkHint) {
  const framework = interval?.framework || frameworkHint || "—";
  const controlId = interval?.control_id || null;

  if (!controlId) return 0;

  const match = controlStatusRows.find(
    (row) =>
      String(row.framework || "") === String(framework || "") &&
      String(row.control_id || "") === String(controlId || "")
  );

  return safeNumber(match?.open_findings_total, 0);
}

function buildDerivedTimeline(legacyDriftEvents, controlStatusRows, freshnessRows) {
  const statusByControl = new Map();
  const freshnessByControlRule = new Map();

  controlStatusRows.forEach((row, index) => {
    const key = `${row.framework || "—"}::${row.control_id || `control-${index}`}`;
    statusByControl.set(key, row);
  });

  freshnessRows.forEach((row, index) => {
    const key = `${row.framework || "—"}::${row.control_id || "—"}::${row.rule_id || `rule-${index}`}`;
    freshnessByControlRule.set(key, row);
  });

  const derivedFromEvents = legacyDriftEvents.map((event, index) => {
    const controlKey = `${event.framework || "—"}::${event.control_id || "—"}`;
    const statusRow = statusByControl.get(controlKey);
    const freshnessKey = `${event.framework || "—"}::${event.control_id || "—"}::${event.code || event.control_id || "—"}`;
    const freshnessRow = freshnessByControlRule.get(freshnessKey);

    const toState = String(event.to_status || "unknown").toLowerCase();
    let severity = "info";
    if (toState === "fail") severity = "high";
    else if (toState === "unknown") severity = "medium";
    else if (String(statusRow?.freshness_state || "").toLowerCase() === "stale") severity = "medium";
    else if (toState === "pass") severity = "low";

    return {
      id: `event-${event.id || index}`,
      source_type: "drift_event",
      framework: event.framework || "—",
      control_id: event.control_id || "—",
      rule_id: event.code || event.control_id || "—",
      resource_id: event.evidence_ref || "—",
      previous_state: event.from_status || "—",
      current_state: event.to_status || "—",
      freshness_state: statusRow?.freshness_state || freshnessRow?.freshness_status || "unknown",
      severity,
      started_at: event.occurred_at || null,
      ended_at: null,
      last_seen_at: statusRow?.last_checked_at || freshnessRow?.last_seen_at || event.occurred_at || null,
      duration_seconds: null,
      summary: `Status changed from ${event.from_status || "unknown"} to ${event.to_status || "unknown"}.`,
      open_findings_total: safeNumber(statusRow?.open_findings_total, 0),
      transition_type: "state_change",
      native_is_open: false,
    };
  });

  const controlsWithEvents = new Set(
    derivedFromEvents.map((item) => `${item.framework}::${item.control_id}`)
  );

  const derivedFromCurrentState = controlStatusRows
    .filter((row) => !controlsWithEvents.has(`${row.framework || "—"}::${row.control_id || "—"}`))
    .map((row, index) => {
      const matchingFreshness = freshnessRows.find(
        (f) =>
          String(f.framework || "") === String(row.framework || "") &&
          String(f.control_id || "") === String(row.control_id || "")
      );

      const state = String(row.state || "unknown").toLowerCase();
      let severity = "info";
      if (state === "fail") severity = "high";
      else if (String(row.freshness_state || "").toLowerCase() === "stale") severity = "medium";
      else if (state === "pass") severity = "low";

      return {
        id: `snapshot-${row.control_id || index}`,
        source_type: "current_state",
        framework: row.framework || "—",
        control_id: row.control_id || "—",
        rule_id: matchingFreshness?.rule_id || row.control_id || "—",
        resource_id: "—",
        previous_state: "—",
        current_state: row.state || "unknown",
        freshness_state: row.freshness_state || matchingFreshness?.freshness_status || "unknown",
        severity,
        started_at: row.last_checked_at || matchingFreshness?.last_seen_at || null,
        ended_at: null,
        last_seen_at: row.last_checked_at || matchingFreshness?.last_seen_at || null,
        duration_seconds: null,
        summary: "Current control state snapshot without a recent drift event in the active window.",
        open_findings_total: safeNumber(row.open_findings_total, 0),
        transition_type: "snapshot",
        native_is_open: false,
      };
    });

  return [...derivedFromEvents, ...derivedFromCurrentState].sort((a, b) => {
    const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
    const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
    return bTime - aTime;
  });
}

function buildNativeTimeline(nativeHistory, controlStatusRows, freshnessRows, frameworkHint) {
  return nativeHistory
    .map((interval, index) => {
      const framework = interval.framework || frameworkHint || "—";
      const controlId = interval.control_id || interval.rule_id || "—";
      const ruleId = interval.rule_id || interval.control_id || "—";
      const currentState = interval.status_to || "unknown";
      const previousState = interval.status_from || "—";
      const freshnessState = inferFreshnessState(interval, freshnessRows, framework);
      const severity = inferSeverityFromNative(interval);
      const isOpen = Boolean(interval.is_open);

      let summary = "Native drift interval from persisted timeline.";
      if (isOpen) {
        summary = `Open drift interval: ${previousState} → ${currentState}.`;
      } else if (interval.drift_end_at) {
        summary = `Closed drift interval: ${previousState} → ${currentState}.`;
      }

      return {
        id: `native-${interval.interval_id || index}`,
        drift_event_id: interval.interval_id || "",
        source_type: "native_interval",
        framework,
        control_id: controlId,
        rule_id: ruleId,
        resource_id: interval.resource_id || interval.evidence_ref || "—",
        previous_state: previousState,
        current_state: currentState,
        freshness_state: freshnessState,
        severity,
        started_at: interval.drift_start_at || interval.changed_at || null,
        ended_at: interval.drift_end_at || null,
        last_seen_at: interval.changed_at || interval.drift_end_at || interval.drift_start_at || null,
        duration_seconds: interval.duration_seconds ?? null,
        summary,
        open_findings_total: inferOpenFindings(
          { ...interval, framework, control_id: controlId },
          controlStatusRows,
          framework
        ),
        transition_type: interval.transition_type || "state_change",
        native_is_open: isOpen,
      };
    })
    .sort((a, b) => {
      const aTime = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const bTime = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      return bTime - aTime;
    });
}

export default function DriftStatePage({
  apiBaseUrl,
  getAccessToken,
  tenantId: tenantIdProp = "",
  userId: userIdProp = "",
  routeParams = {},
  setMode,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();

  const [framework, setFramework] = useState(String(routeParams.framework || "SOC2").trim().toUpperCase());
  // Organization applied-framework authority. Never expose the global catalog on failure.
  const [frameworkScope, setFrameworkScope] = useState(null);
  const [limit, setLimit] = useState(10);
  const [hours, setHours] = useState(safeNumber(routeParams.hours, 168) || 168);
  const [sourceFilter, setSourceFilter] = useState(
    String(routeParams.sourceFilter || routeParams.source_filter || "all").toLowerCase()
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [panelWarnings, setPanelWarnings] = useState([]);

  const [nativeSummary, setNativeSummary] = useState({});
  const [nativeRecent, setNativeRecent] = useState([]);
  const [nativeOpen, setNativeOpen] = useState([]);
  const [nativeHistory, setNativeHistory] = useState([]);
  const [legacyDriftEvents, setLegacyDriftEvents] = useState([]);
  const [controlStatusRows, setControlStatusRows] = useState([]);
  const [freshnessRows, setFreshnessRows] = useState([]);
  const [identityFindings, setIdentityFindings] = useState([]);
  const [selectedDriftId, setSelectedDriftId] = useState(null);
  const [highlightedDriftId, setHighlightedDriftId] = useState(null);

  const timelineSectionRef = useRef(null);
  const selectedTimelineRowRef = useRef(null);
  const routedScrollHandledRef = useRef("");
  const routedSelectionHandledRef = useRef("");

  const routedRuleId = String(routeParams.ruleId || routeParams.rule_id || "").toUpperCase();
  const routedResourceId = String(routeParams.resourceId || routeParams.resource_id || "").trim();
  const routedControlId = String(routeParams.controlId || routeParams.control_id || "").trim();
  const routedSection = String(routeParams.section || "").trim().toLowerCase();
  const hasRoutedDriftTarget = Boolean(
    routedRuleId ||
      routedResourceId ||
      routedControlId ||
      ["identity", "code", "edge", "code_edge"].includes(routedSection)
  );

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId = String(tenantIdProp || "").trim();
  const userId = String(userIdProp || "").trim();

  useEffect(() => {
    const nextRouteParams = {
      framework,
      limit: String(limit),
      hours: String(hours),
      sourceFilter,
    };

    if (routedRuleId) nextRouteParams.ruleId = routedRuleId;
    if (routedResourceId) nextRouteParams.resourceId = routedResourceId;
    if (routedControlId) nextRouteParams.controlId = routedControlId;
    if (routedSection) nextRouteParams.section = routedSection;
    if (routeParams.source) nextRouteParams.source = routeParams.source;
    if (routeParams.sourceMode) nextRouteParams.sourceMode = routeParams.sourceMode;
    if (routeParams.returnMode) nextRouteParams.returnMode = routeParams.returnMode;
    if (routeParams.returnTo) nextRouteParams.returnTo = routeParams.returnTo;
    if (routeParams.routeNavNonce) nextRouteParams.routeNavNonce = routeParams.routeNavNonce;

    setMode?.("drift", nextRouteParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framework, limit, hours, sourceFilter]);

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") return await getAccessToken();
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  const requestJson = useCallback(
    async (path, options = {}) => {
      const token = await fetchToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(`${normalizedBase}${path}`, {
        method: options.method || "GET",
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [fetchToken, normalizedBase, tenantId, userId]
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setFrameworkScope(null);
    if (!tenantId || !userId) {
      setFrameworkScope({ tenantId, frameworks: [], error: "Organization identity is unavailable." });
      return () => { active = false; controller.abort(); };
    }
    (async () => {
      try {
        const token = await fetchToken();
        const result = await getJsonWithToken(token, "/dashboard/applied-frameworks", {}, controller.signal);
        if (!active) return;
        const frameworks = Array.isArray(result?.frameworks)
          ? [...new Set(result.frameworks.map((value) => String(value || "").trim().toUpperCase()).filter(Boolean))]
          : [];
        if (!frameworks.length) throw new Error("No applied frameworks are configured for this organization.");
        setFrameworkScope({ tenantId, frameworks, error: "" });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setFrameworkScope({ tenantId, frameworks: [], error: String(error?.message || error) });
      }
    })();
    return () => { active = false; controller.abort(); };
  }, [fetchToken, getJsonWithToken, tenantId, userId]);

  const appliedFrameworks = frameworkScope?.tenantId === tenantId && !frameworkScope.error
    ? frameworkScope.frameworks : [];
  const frameworkAuthorized = appliedFrameworks.includes(String(framework || "").toUpperCase());

  useEffect(() => {
    if (!appliedFrameworks.length) return;
    setFramework((current) => appliedFrameworks.includes(String(current || "").toUpperCase())
      ? current : appliedFrameworks[0]);
  }, [frameworkScope, tenantId]);

  useEffect(() => {
    if (!frameworkAuthorized) {
      setLoading(false);
      return undefined;
    }
    let mounted = true;
    setLoading(true);
    setErr(null);
    setPanelWarnings([]);

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("drift-state-page-timeout"),
      REQUEST_TIMEOUT_MS
    );

    (async () => {
      try {
        const token = await fetchToken();

        const requests = [
          {
            key: "nativeSummary",
            label: "Native drift summary",
            required: false,
            promise: getJsonWithToken(
              token,
              "/drift/summary",
              { framework },
              controller.signal
            ),
          },
          {
            key: "nativeRecent",
            label: "Native recent drift timeline",
            required: false,
            promise: getJsonWithToken(
              token,
              "/drift/timeline/recent",
              { framework, limit: 500 },
              controller.signal
            ),
          },
          {
            key: "nativeOpen",
            label: "Native open drift timeline",
            required: false,
            promise: getJsonWithToken(
              token,
              "/drift/timeline/open",
              { framework, limit: 500 },
              controller.signal
            ),
          },
          {
            key: "nativeHistory",
            label: "Native drift history",
            required: false,
            promise: getJsonWithToken(
              token,
              "/drift/timeline/history",
              { framework, limit: 500 },
              controller.signal
            ),
          },
          {
            key: "legacyDriftRecent",
            label: "Legacy recent drift events",
            required: false,
            promise: getJsonWithToken(
              token,
              "/controls/drift/recent",
              { framework, limit, hours },
              controller.signal
            ),
          },
          {
            key: "controlStatus",
            label: "Control status snapshot",
            required: true,
            promise: getJsonWithToken(
              token,
              "/dashboard/control-status/latest",
              { framework, limit: 150 },
              controller.signal
            ),
          },
          {
            key: "controlFreshness",
            label: "Control freshness",
            required: true,
            promise: getJsonWithToken(
              token,
              "/dashboard/control-freshness",
              { framework },
              controller.signal
            ),
          },
          {
            key: "identityFindings",
            label: "Identity posture findings",
            required: false,
            promise: getJsonWithToken(
              token,
              "/normalize/findings",
              { limit: 250 },
              controller.signal
            ),
          },
        ];

        const settled = await Promise.allSettled(requests.map((r) => r.promise));
        if (!mounted) return;

        let requiredFailure = null;
        const nextWarnings = [];

        let nextNativeSummary = {};
        let nextNativeRecent = [];
        let nextNativeOpen = [];
        let nextNativeHistory = [];
        let nextLegacyDriftEvents = [];
        let nextControlStatus = [];
        let nextFreshness = [];
        let nextIdentityFindings = [];

        settled.forEach((result, index) => {
          const req = requests[index];

          if (result.status === "fulfilled") {
            if (req.key === "nativeSummary") nextNativeSummary = normalizeNativeSummary(result.value);
            if (req.key === "nativeRecent") nextNativeRecent = normalizeNativeHistory(result.value);
            if (req.key === "nativeOpen") nextNativeOpen = normalizeNativeHistory(result.value);
            if (req.key === "nativeHistory") nextNativeHistory = normalizeNativeHistory(result.value);
            if (req.key === "legacyDriftRecent") nextLegacyDriftEvents = normalizeLegacyDriftEvents(result.value);
            if (req.key === "controlStatus") nextControlStatus = normalizeControlStatus(result.value);
            if (req.key === "controlFreshness") nextFreshness = normalizeFreshness(result.value);
            if (req.key === "identityFindings") nextIdentityFindings = normalizeIdentityFindings(result.value);
            return;
          }

          const reason = result.reason;
          const message =
            reason?.name === "AbortError"
              ? `${req.label} timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
              : String(reason?.message || reason || `${req.label} failed.`);

          if (req.required) {
            requiredFailure = `${req.label} failed: ${message}`;
          } else {
            nextWarnings.push(`${req.label} unavailable: ${message}`);
          }
        });

        if (requiredFailure) throw new Error(requiredFailure);

        setNativeSummary(nextNativeSummary);
        setNativeRecent(nextNativeRecent);
        setNativeOpen(nextNativeOpen);
        setNativeHistory(nextNativeHistory);
        setLegacyDriftEvents(nextLegacyDriftEvents);
        setControlStatusRows(nextControlStatus);
        setFreshnessRows(nextFreshness);
        setIdentityFindings(nextIdentityFindings);

        const hasNativeTimeline =
          nextNativeHistory.length > 0 || nextNativeRecent.length > 0 || nextNativeOpen.length > 0;

        if (!hasNativeTimeline) {
          nextWarnings.push("Using derived fallback timeline because native drift history returned no rows.");
        }

        setPanelWarnings(nextWarnings);

        const preferredTimeline = hasNativeTimeline
          ? buildNativeTimeline(
              nextNativeHistory.length > 0 ? nextNativeHistory : nextNativeRecent,
              nextControlStatus,
              nextFreshness,
              framework
            )
          : buildDerivedTimeline(nextLegacyDriftEvents, nextControlStatus, nextFreshness);

        if (preferredTimeline.length > 0) {
          setSelectedDriftId((prev) => prev || preferredTimeline[0].id);
        } else {
          setSelectedDriftId(null);
        }
      } catch (e) {
        if (!mounted) return;
        console.error("Drift & State page load error:", e);
        setErr(String(e?.message || e));
      } finally {
        clearTimeout(timeoutId);
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchToken, getJsonWithToken, framework, frameworkAuthorized, limit, hours]);

  const fallbackTimeline = useMemo(() => {
    return buildDerivedTimeline(legacyDriftEvents, controlStatusRows, freshnessRows);
  }, [legacyDriftEvents, controlStatusRows, freshnessRows]);

  const nativeTimeline = useMemo(() => {
    const base = nativeHistory.length > 0 ? nativeHistory : nativeRecent;
    return buildNativeTimeline(base, controlStatusRows, freshnessRows, framework);
  }, [nativeHistory, nativeRecent, controlStatusRows, freshnessRows, framework]);

  const identityTimeline = useMemo(() => {
    return buildIdentityTimeline(identityFindings, controlStatusRows, freshnessRows, framework);
  }, [identityFindings, controlStatusRows, freshnessRows, framework]);

  const codeEdgeTimeline = useMemo(() => {
    return buildCodeEdgeTimeline(identityFindings, controlStatusRows, freshnessRows, framework);
  }, [identityFindings, controlStatusRows, freshnessRows, framework]);

  const usingNativeTimeline = nativeTimeline.length > 0;

  const preferredTimeline = useMemo(() => {
    const operationalTimeline = usingNativeTimeline ? nativeTimeline : fallbackTimeline;
    const combined = [...identityTimeline, ...codeEdgeTimeline, ...operationalTimeline];

    return combined.sort((a, b) => {
      const importantSourceTypes = new Set(["identity_finding", "code_finding", "edge_finding"]);
      const aPriority = importantSourceTypes.has(a.source_type) && String(a.current_state || "").toLowerCase() === "fail" ? 1 : 0;
      const bPriority = importantSourceTypes.has(b.source_type) && String(b.current_state || "").toLowerCase() === "fail" ? 1 : 0;

      if (aPriority !== bPriority) return bPriority - aPriority;

      const aSeverity = { critical: 4, high: 3, medium: 2, low: 1, info: 0 }[String(a.severity || "").toLowerCase()] || 0;
      const bSeverity = { critical: 4, high: 3, medium: 2, low: 1, info: 0 }[String(b.severity || "").toLowerCase()] || 0;
      if (aSeverity !== bSeverity) return bSeverity - aSeverity;

      const aTime = a.last_seen_at || a.started_at ? new Date(a.last_seen_at || a.started_at).getTime() : 0;
      const bTime = b.last_seen_at || b.started_at ? new Date(b.last_seen_at || b.started_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [usingNativeTimeline, nativeTimeline, fallbackTimeline, identityTimeline, codeEdgeTimeline]);

  // Native Drift history endpoints do not accept hours. Apply the selected
  // window to the retrieved intervals, without changing the persisted history.
  // Open intervals are included if they started or were last seen in-window.
  const windowedTimeline = useMemo(() => {
    const cutoff = Date.now() - hours * 60 * 60 * 1000;
    return preferredTimeline.filter((item) => {
      const observed = item.last_seen_at || item.started_at;
      const timestamp = observed ? new Date(observed).getTime() : NaN;
      return Number.isFinite(timestamp) && timestamp >= cutoff;
    });
  }, [preferredTimeline, hours]);

  const sourceFilterCounts = useMemo(() => {
    if (!frameworkAuthorized) return { all: 0, drift: 0, identity: 0, code: 0, edge: 0, ec2: 0 };
    return {
      all: windowedTimeline.length,
      drift: windowedTimeline.filter((item) => timelineMatchesSourceFilter(item, "drift")).length,
      identity: windowedTimeline.filter((item) => timelineMatchesSourceFilter(item, "identity")).length,
      code: windowedTimeline.filter((item) => timelineMatchesSourceFilter(item, "code")).length,
      edge: windowedTimeline.filter((item) => timelineMatchesSourceFilter(item, "edge")).length,
      ec2: windowedTimeline.filter((item) => timelineMatchesSourceFilter(item, "ec2")).length,
    };
  }, [windowedTimeline, frameworkAuthorized]);

  const matchingTimeline = useMemo(() => {
    if (!frameworkAuthorized) return [];
    return windowedTimeline.filter((item) => timelineMatchesSourceFilter(item, sourceFilter));
  }, [windowedTimeline, sourceFilter, frameworkAuthorized]);

  const sourceFilteredTimeline = useMemo(() => matchingTimeline.slice(0, limit), [matchingTimeline, limit]);

  useEffect(() => {
    if (!sourceFilteredTimeline.length) {
      setSelectedDriftId(null);
      return;
    }

    const routeMatchesItem = (item) => {
      const ruleMatches =
        routedRuleId && String(item.rule_id || "").toUpperCase() === routedRuleId;
      const resourceMatches =
        routedResourceId && String(item.resource_id || "") === routedResourceId;
      const controlMatches =
        routedControlId && String(item.control_id || "") === routedControlId;

      if (routedSection === "identity" && item.source_type === "identity_finding") {
        return ruleMatches || (resourceMatches && controlMatches) || resourceMatches;
      }

      return ruleMatches || (resourceMatches && controlMatches) || resourceMatches;
    };

    const routedSelectionKey = [
      routedSection || "drift",
      routedRuleId,
      routedResourceId,
      routedControlId,
      routeParams.routeNavNonce || routeParams.navNonce || "",
    ].join("::");

    const routeSelectionAlreadyHandled =
      routedSelectionHandledRef.current === routedSelectionKey;

    const routedCandidates = hasRoutedDriftTarget && !routeSelectionAlreadyHandled
      ? sourceFilteredTimeline.filter(routeMatchesItem)
      : [];

    const routedMatch =
      routedCandidates.find((item) => item.source_type === "identity_finding") ||
      routedCandidates[0] ||
      (!routeSelectionAlreadyHandled && routedSection === "identity"
        ? sourceFilteredTimeline.find((item) => item.source_type === "identity_finding")
        : !routeSelectionAlreadyHandled && routedSection === "code"
        ? sourceFilteredTimeline.find((item) => item.source_type === "code_finding")
        : !routeSelectionAlreadyHandled && routedSection === "edge"
        ? sourceFilteredTimeline.find((item) => item.source_type === "edge_finding")
        : !routeSelectionAlreadyHandled && routedSection === "code_edge"
        ? sourceFilteredTimeline.find((item) =>
            ["code_finding", "edge_finding"].includes(item.source_type)
          )
        : null);

    if (routedMatch) {
      routedSelectionHandledRef.current = routedSelectionKey;

      if (selectedDriftId !== routedMatch.id) {
        setSelectedDriftId(routedMatch.id);
        return;
      }
    }

    const exists = sourceFilteredTimeline.some((item) => item.id === selectedDriftId);
    if (!exists) setSelectedDriftId(sourceFilteredTimeline[0].id);
  }, [
    hasRoutedDriftTarget,
    sourceFilteredTimeline,
    routedControlId,
    routedResourceId,
    routedRuleId,
    routedSection,
    selectedDriftId,
    routeParams.navNonce,
    routeParams.routeNavNonce,
  ]);

  const selectedItem = useMemo(() => {
    return sourceFilteredTimeline.find((item) => item.id === selectedDriftId) || sourceFilteredTimeline[0] || null;
  }, [sourceFilteredTimeline, selectedDriftId]);

  useEffect(() => {
    const driftEventId =
      selectedItem?.source_type === "native_interval"
        ? String(selectedItem?.drift_event_id || "").trim()
        : "";

    const pageContext = {
      page: "drift",
      surface: "drift_page",
      intelligence_mode: driftEventId ? "contextual_explain" : "page",
      framework,
      window: `${hours}h`,
      ...(sourceFilter ? { filter: sourceFilter } : {}),
    };

    if (!driftEventId) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    setCopilotContext({
      ...pageContext,
      record_type: "drift_event",
      drift_event_id: driftEventId,
      focus_label: String(
        selectedItem?.rule_id || selectedItem?.control_id || "Selected drift interval"
      ),
      focus_type: "Drift interval",
    });
  }, [
    clearCopilotContext,
    framework,
    hours,
    selectedItem,
    setCopilotContext,
    sourceFilter,
  ]);

  useEffect(() => {
    if (!hasRoutedDriftTarget || !selectedItem) return undefined;

    const selectedMatchesRoute =
      (routedRuleId && String(selectedItem.rule_id || "").toUpperCase() === routedRuleId) ||
      (routedResourceId && String(selectedItem.resource_id || "") === routedResourceId) ||
      (routedControlId && String(selectedItem.control_id || "") === routedControlId) ||
      (routedSection === "identity" && selectedItem.source_type === "identity_finding");

    if (!selectedMatchesRoute) return undefined;

    const navigationKey = [
      routedSection || "drift",
      routedRuleId,
      routedResourceId,
      routedControlId,
      routeParams.routeNavNonce || routeParams.navNonce || "",
      selectedItem.id,
    ].join("::");

    if (routedScrollHandledRef.current === navigationKey) return undefined;
    routedScrollHandledRef.current = navigationKey;

    setHighlightedDriftId(selectedItem.id);

    const scrollToSelectedRow = () => {
      const target = selectedTimelineRowRef.current || timelineSectionRef.current;
      if (!target) return;

      try {
        target.scrollIntoView({ behavior: "auto", block: "center" });
      } catch {
        const top = target.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo(0, Math.max(0, top));
      }
    };

    const firstFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToSelectedRow);
    });
    const shortDelay = window.setTimeout(scrollToSelectedRow, 80);
    const layoutDelay = window.setTimeout(scrollToSelectedRow, 180);
    const highlightDelay = window.setTimeout(() => setHighlightedDriftId(null), 2400);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.clearTimeout(shortDelay);
      window.clearTimeout(layoutDelay);
      window.clearTimeout(highlightDelay);
    };
  }, [
    hasRoutedDriftTarget,
    routeParams.navNonce,
    routeParams.routeNavNonce,
    routedControlId,
    routedResourceId,
    routedRuleId,
    routedSection,
    selectedItem,
  ]);

  const recentTransitions = useMemo(() => {
    if (nativeRecent.length > 0) {
      return nativeRecent.map((interval, index) => ({
        id: interval.interval_id || `native-recent-${index}`,
        control_id: interval.control_id || interval.rule_id || "—",
        rule_id: interval.rule_id || interval.control_id || "—",
        framework: interval.framework || framework || "—",
        from_state: interval.status_from || "—",
        to_state: interval.status_to || "—",
        changed_at: interval.changed_at || interval.drift_end_at || interval.drift_start_at || null,
        resource_id: interval.resource_id || interval.evidence_ref || "—",
      }));
    }

    return legacyDriftEvents.map((event) => ({
      id: event.id,
      control_id: event.control_id || "—",
      rule_id: event.code || event.control_id || "—",
      framework: event.framework || "—",
      from_state: event.from_status || "—",
      to_state: event.to_status || "—",
      changed_at: event.occurred_at || null,
      resource_id: event.evidence_ref || "—",
    }));
  }, [nativeRecent, legacyDriftEvents, framework]);

  const metrics = useMemo(() => {
    const staleFromFreshness = freshnessRows.filter(
      (item) => String(item.freshness_status || "").toLowerCase() === "stale"
    ).length;

    const fallbackDriftedCount = preferredTimeline.filter((item) =>
      ["fail", "drifted", "noncompliant"].includes(String(item.current_state || "").toLowerCase())
    ).length;

    return {
      driftedCount: safeNumber(nativeSummary.active_open_count, 0) || fallbackDriftedCount,
      staleCount: staleFromFreshness || safeNumber(nativeSummary.stale_transition_count, 0),
      currentSnapshots: controlStatusRows.length,
      transitionCount: recentTransitions.length,
      timelineRows: sourceFilteredTimeline.length,
      openNativeCount: nativeOpen.length,
      identityFindingsCount: identityTimeline.length,
      identityFailedCount: identityTimeline.filter((item) => String(item.current_state || "").toLowerCase() === "fail").length,
      codeFindingsCount: codeEdgeTimeline.filter((item) => item.source_type === "code_finding").length,
      codeFailedCount: codeEdgeTimeline.filter((item) => item.source_type === "code_finding" && String(item.current_state || "").toLowerCase() === "fail").length,
      edgeFindingsCount: codeEdgeTimeline.filter((item) => item.source_type === "edge_finding").length,
      edgeFailedCount: codeEdgeTimeline.filter((item) => item.source_type === "edge_finding" && String(item.current_state || "").toLowerCase() === "fail").length,
      latestChangeAt: nativeSummary.latest_change_at || null,
    };
  }, [nativeSummary, freshnessRows, preferredTimeline, sourceFilteredTimeline, controlStatusRows, recentTransitions, nativeOpen, identityTimeline, codeEdgeTimeline]);

  const transitionsHelper = useMemo(() => {
    if (metrics.transitionCount === 0) {
      return "No drift events were recorded in the selected time window.";
    }
    return `${metrics.transitionCount} recent drift transition${
      metrics.transitionCount === 1 ? "" : "s"
    } were returned for the selected window.`;
  }, [metrics.transitionCount]);

  if (loading) {
    return (
      <div className="drift-state-page oasys-page-canvas">
        <div className="drift-page__shell">
          <header className="oasys-page-header drift-page-header">
            <div className="oasys-page-header__content">
              <h1 className="oasys-page-header__title">Drift &amp; State</h1>
              <p className="oasys-page-header__description">
                Track recent drift transitions and compare them against current control state and freshness truth.
              </p>
            </div>
          </header>
          <Card tone="primary" density="comfortable" className="drift-state-card">
            Loading drift and state…
          </Card>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="drift-state-page oasys-page-canvas">
        <div className="drift-page__shell">
          <header className="oasys-page-header drift-page-header">
            <div className="oasys-page-header__content">
              <h1 className="oasys-page-header__title">Drift &amp; State</h1>
              <p className="oasys-page-header__description">
                Track recent drift transitions and compare them against current control state and freshness truth.
              </p>
            </div>
          </header>
          <Card tone="primary" density="comfortable" className="drift-state-card drift-state-card--error">
            Failed to load Drift &amp; State: {err}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="drift-state-page oasys-page-canvas">
      <div className="drift-page__shell">
        <header className="oasys-page-header drift-page-header">
          <div className="oasys-page-header__content">
            <h1 className="oasys-page-header__title">Drift &amp; State</h1>
            <p className="oasys-page-header__description">
              Track recent drift transitions and compare them against current control state and freshness truth.
            </p>
          </div>

          <div className="drift-page-header__actions">
            <Button variant="secondary" size="medium" onClick={() => setMode("dashboard")}>
              Mission Control
            </Button>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setMode("posture", { framework })}
            >
              Open Posture
            </Button>
          </div>
        </header>

        {panelWarnings.length > 0 ? (
          <Card tone="secondary" density="compact" className="drift-warning-card">
            <div className="drift-warning-card__heading">
              <StatusChip tone="warning">Partial data</StatusChip>
              <strong>Drift &amp; State loaded with partial data</strong>
            </div>
            <div className="drift-warning-card__items">
              {panelWarnings.map((warning, index) => (
                <span key={`${warning}-${index}`}>{warning}</span>
              ))}
            </div>
          </Card>
        ) : null}

        <DriftIntelligencePanel
          requestJson={requestJson}
          framework={framework}
          refreshKey={`${framework}:${metrics.latestChangeAt || "none"}:${metrics.timelineRows}`}
        />

        <Section
          title="Drift Overview"
          description="Current drift pressure across controls, freshness, identity, code, and edge posture."
          density="compact"
          className="drift-overview-section"
        >
          <div className="drift-metric-rail">
            <MetricCard
              label="Drifted"
              value={metrics.driftedCount}
              footnote={usingNativeTimeline ? "native open intervals" : "derived open items"}
              tone={metrics.driftedCount > 0 ? "danger" : "ok"}
            />
            <MetricCard
              label="Stale"
              value={metrics.staleCount}
              footnote="freshness marked stale"
              tone={metrics.staleCount > 0 ? "warn" : "ok"}
            />
            <MetricCard
              label="Transitions"
              value={metrics.transitionCount}
              footnote="recent state changes"
            />
            <MetricCard
              label="Identity"
              value={metrics.identityFindingsCount}
              footnote={`${metrics.identityFailedCount} failed`}
              tone={metrics.identityFailedCount > 0 ? "danger" : "ok"}
            />
            <MetricCard
              label="Code"
              value={metrics.codeFindingsCount}
              footnote={`${metrics.codeFailedCount} failed`}
              tone={metrics.codeFailedCount > 0 ? "danger" : "ok"}
            />
            <MetricCard
              label="Edge"
              value={metrics.edgeFindingsCount}
              footnote={`${metrics.edgeFailedCount} failed`}
              tone={metrics.edgeFailedCount > 0 ? "danger" : "ok"}
            />
          </div>
        </Section>

        <Section
          title="Drift Timeline"
          description={
            usingNativeTimeline
              ? "Persisted drift intervals with identity, code, and edge posture findings overlaid."
              : "Recent drift events combined with current state, freshness, identity, code, and edge posture."
          }
          badge={`${sourceFilteredTimeline.length} of ${matchingTimeline.length} visible`}
          badgeTone="informational"
          density="compact"
          className="drift-workbench-section"
        >
          <div className="drift-filter-toolbar">
            <FilterGroup label="Framework">
              {!appliedFrameworks.length ? (
                <span role={frameworkScope?.error ? "alert" : "status"}>
                  {frameworkScope?.tenantId === tenantId && frameworkScope.error
                    ? `Framework authority unavailable: ${frameworkScope.error}`
                    : "Loading organization frameworks…"}
                </span>
              ) : appliedFrameworks.map((item) => (
                <Button
                  key={item}
                  variant={framework === item ? "primary" : "secondary"}
                  size="small"
                  onClick={() => { setFramework(item); setLimit(10); }}
                  aria-pressed={framework === item}
                >
                  {item}
                </Button>
              ))}
            </FilterGroup>

            <FilterGroup label="Window">
              {[24, 72, 168, 720].map((value) => (
                <Button
                  key={value}
                  variant={hours === value ? "primary" : "secondary"}
                  size="small"
                  onClick={() => { setHours(value); setLimit(10); }}
                  aria-pressed={hours === value}
                >
                  {value === 168 ? "7 days" : value === 720 ? "30 days" : `${value}h`}
                </Button>
              ))}
            </FilterGroup>

            <FilterGroup label="Source">
              {[
                ["all", "All", sourceFilterCounts.all],
                ["drift", "Controls", sourceFilterCounts.drift],
                ["identity", "Identity", sourceFilterCounts.identity],
                ["code", "Code", sourceFilterCounts.code],
                ["edge", "Edge", sourceFilterCounts.edge],
                ["ec2", "EC2", sourceFilterCounts.ec2],
              ].map(([key, label, count]) => (
                <Button
                  key={key}
                  variant={sourceFilter === key ? "primary" : "secondary"}
                  size="small"
                  onClick={() => { setSourceFilter(key); setLimit(10); }}
                  aria-pressed={sourceFilter === key}
                >
                  {label} {count}
                </Button>
              ))}
            </FilterGroup>
          </div>

          <div className="drift-workbench" ref={timelineSectionRef}>
            <div className="drift-timeline-column">
              <div className="drift-timeline-column__header">
                <div>
                  <strong>{usingNativeTimeline ? "Native timeline" : "Derived timeline"}</strong>
                  <span>Choose an item to inspect state, evidence, and linked operations.</span>
                </div>
              </div>

              <div className="drift-timeline-expanded" style={{ height: "auto", maxHeight: "none", overflow: "visible" }}>
                {sourceFilteredTimeline.length === 0 ? (
                  <div className="drift-empty-state">
                    No drift or state rows matched the selected framework and source filter.
                  </div>
                ) : (
                  sourceFilteredTimeline.map((item) => {
                    const isSelected = selectedItem?.id === item.id;
                    const isHighlighted = highlightedDriftId === item.id;
                    const displayTitle = buildDisplayTitle(
                      item.display_control_label || item.control_id,
                      item.rule_id
                    );

                    return (
                      <div
                        key={item.id}
                        ref={isSelected ? selectedTimelineRowRef : null}
                        className="drift-timeline-row-anchor"
                      >
                        <Card
                          as="button"
                          type="button"
                          tone="secondary"
                          density="compact"
                          interactive
                          selected={isSelected}
                          className={[
                            "drift-timeline-row",
                            isHighlighted ? "is-highlighted" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => {
                            setSelectedDriftId(item.id);
                            setHighlightedDriftId(null);
                          }}
                        >
                          <div className="drift-timeline-row__chips">
                            <SourcePill value={item.source_type} />
                            <SeverityPill value={item.severity} />
                            <StatePill value={item.current_state} />
                            <StatePill value={item.freshness_state} />
                          </div>

                          <strong className="drift-timeline-row__title">{displayTitle}</strong>

                          <div className="drift-timeline-row__meta">
                            <span>{item.framework}</span>
                            <span>{formatRelativeTime(item.started_at || item.last_seen_at)}</span>
                            <span>Open findings: {safeNumber(item.open_findings_total, 0)}</span>
                          </div>

                          <p className="drift-timeline-row__summary">{item.summary}</p>
                        </Card>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="drift-timeline-load-more" style={{ padding: "12px 0", textAlign: "center" }}>
                <div style={{ marginBottom: 8 }}>
                  Showing {sourceFilteredTimeline.length} of {matchingTimeline.length} matching intervals
                </div>
                {sourceFilteredTimeline.length < matchingTimeline.length ? (
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={loading}
                    onClick={() => setLimit((current) => current + 10)}
                  >
                    Load 10 more
                  </Button>
                ) : null}
              </div>
            </div>

            <Card tone="primary" density="comfortable" className="drift-detail-panel">
              <div className="drift-detail-panel__header">
                <div>
                  <span className="drift-detail-panel__label">Selected Drift Item</span>
                  {selectedItem ? (
                    <strong>
                      {buildDisplayTitle(
                        selectedItem.display_control_label || selectedItem.control_id,
                        selectedItem.rule_id
                      )}
                    </strong>
                  ) : null}
                </div>
              </div>

              {!selectedItem ? (
                <div className="drift-empty-state">
                  Select a drift item to inspect details.
                </div>
              ) : (
                <div className="drift-detail-panel__body">
                  <div className="drift-detail-panel__chips">
                    <SourcePill value={selectedItem.source_type} />
                    <SeverityPill value={selectedItem.severity} />
                    <StatePill value={selectedItem.current_state} />
                    <StatePill value={selectedItem.freshness_state} />
                  </div>

                  <div className="drift-detail-panel__context">
                    <span>{cleanDisplayValue(selectedItem.framework)}</span>
                    <span>{cleanDisplayValue(selectedItem.source_type)}</span>
                  </div>

                  <p className="drift-detail-panel__summary">
                    {selectedItem.summary || "No drift detail summary available."}
                  </p>

                  <div className="drift-detail-panel__meta-grid">
                    <MetaField label="Previous" value={cleanDisplayValue(selectedItem.previous_state)} />
                    <MetaField label="Current" value={cleanDisplayValue(selectedItem.current_state, "unknown")} />
                    <MetaField label="Started" value={formatTimestamp(selectedItem.started_at)} />
                    <MetaField label="Ended" value={formatTimestamp(selectedItem.ended_at)} />
                    <MetaField label="Last Seen" value={formatTimestamp(selectedItem.last_seen_at)} />
                    <MetaField label="Freshness" value={cleanDisplayValue(selectedItem.freshness_state, "unknown")} />
                    <MetaField
                      label="Provider"
                      value={normalizeIdentitySource(selectedItem.provider || selectedItem.source_type)}
                      wrap
                    />
                    <MetaField
                      label="Stream"
                      value={cleanDisplayValue(selectedItem.stream || selectedItem.source_type)}
                      wrap
                    />
                    <MetaField
                      label="Evidence / Resource"
                      value={buildEvidenceLabel(selectedItem.resource_id)}
                      wrap
                    />
                    <MetaField label="Duration" value={formatDuration(selectedItem.duration_seconds)} />
                  </div>

                  <div className="drift-detail-panel__actions">
                    <Button
                      variant="primary"
                      size="medium"
                      fullWidth
                      onClick={() =>
                        setMode("posture", {
                          framework: selectedItem.framework || framework,
                          controlId: selectedItem.control_id || "",
                          ruleId: selectedItem.rule_id || "",
                          resourceId: selectedItem.resource_id || "",
                          section: "investigation_summary",
                          source: "drift_state",
                          routeNavNonce: String(Date.now()),
                        })
                      }
                    >
                      Open in Posture
                    </Button>

                    <Button
                      variant="secondary"
                      size="medium"
                      fullWidth
                      onClick={() =>
                        setMode("alerts", {
                          framework: selectedItem.framework || framework,
                          controlId: selectedItem.control_id || "",
                          ruleId: selectedItem.rule_id || "",
                          resourceId: selectedItem.resource_id || "",
                        })
                      }
                    >
                      Open Alerts
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </Section>

        <Section
          title="Current State Snapshot"
          description="Current control state from the dashboard read layer."
          density="compact"
          className="drift-state-snapshot-section"
        >
          <div className="drift-table-wrap">
            <table className="drift-table">
              <thead>
                <tr>
                  <th>Control</th>
                  <th>State</th>
                  <th>Freshness</th>
                  <th>Last Checked</th>
                  <th>Open Findings</th>
                </tr>
              </thead>
              <tbody>
                {controlStatusRows.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="drift-empty-state">No control status rows were returned.</div>
                    </td>
                  </tr>
                ) : (
                  controlStatusRows.map((row, index) => (
                    <tr key={`${row.control_id || "control"}-${index}`}>
                      <td>{cleanDisplayValue(row.control_id)}</td>
                      <td><StatePill value={cleanDisplayValue(row.state, "unknown")} /></td>
                      <td><StatePill value={cleanDisplayValue(row.freshness_state, "unknown")} /></td>
                      <td>{formatTimestamp(row.last_checked_at)}</td>
                      <td>{safeNumber(row.open_findings_total, 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Section>

        <Section
          title="Recent Transitions"
          description={
            usingNativeTimeline
              ? "Native recent intervals from the drift timeline read layer."
              : "Legacy recent drift events from the control routes layer."
          }
          badge={metrics.transitionCount > 0 ? `${metrics.transitionCount} recent` : undefined}
          badgeTone="informational"
          density="compact"
          className="drift-transitions-section"
        >
          {recentTransitions.length === 0 ? (
            <div className="drift-empty-state drift-empty-state--compact">
              <strong>No recent transitions</strong>
              <span>{transitionsHelper}</span>
            </div>
          ) : (
            <div className="drift-table-wrap">
              <table className="drift-table drift-table--transitions">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Rule</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Changed</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransitions.map((row) => (
                    <tr key={row.id}>
                      <td>{cleanDisplayValue(row.control_id)}</td>
                      <td>
                        {buildDisplayTitle(
                          row.rule_id && row.rule_id !== row.control_id ? "" : row.control_id,
                          row.rule_id
                        )}
                      </td>
                      <td>{cleanDisplayValue(row.from_state)}</td>
                      <td>{cleanDisplayValue(row.to_state)}</td>
                      <td>{formatTimestamp(row.changed_at)}</td>
                      <td className="drift-table__evidence">
                        {buildEvidenceLabel(row.resource_id)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
